import {
  ReportType,
  type Report,
  type ReportConfiguration,
  type ReportTemplate,
} from "../../../../../../api/types/svTypes.ts";
import { createChartReportConfiguration } from "../../components/builder/utils/configuration/chartConfiguration";
import { createFinancialStatementReportConfiguration } from "../../components/builder/utils/configuration/financialStatementConfiguration";
import { createListReportConfiguration } from "../../components/builder/utils/configuration/listConfiguration";
import { createPivotReportConfiguration } from "../../components/builder/utils/configuration/pivotConfigurations";
import { createTabularReportConfiguration } from "../../components/builder/utils/configuration/tabularConfiguration";
import { createTileReportConfiguration } from "../../components/builder/utils/configuration/tileConfiguration";
import { createTabularDefaultSettings } from "../../utilities/isConfigurationValid.ts";
import {
  isChartReportType,
  isFinancialStatementReportType,
  isListReportType,
  isPivotReportType,
  usesTabularConfiguration,
  isTileReportType,
} from "../../utilities/reportTypeHelper.ts";
import { selectIsReportChanged } from "../selectors/currentReportSelectors.ts";
import { selectFieldsStateConfiguration, selectReportType } from "../selectors/fieldsStateSelectors.ts";
import { selectCurrentMetaDataSetId } from "../selectors/measureDataSetsSelectors.ts";
import type { ReportConfigurationState } from "../types/versionHistoryState.types.ts";
import { createTypedAsyncThunk } from "../utils/createTypedAsyncThunk";
import { createCurrentReportAction, createFieldsStateAction, createMetaDataAction } from "../utils/storeUtils";
import { baselineEditingHistory } from "./editingHistoryThunks";
import { fieldsStateActions } from "./fieldStatesThunks";
import { changeDataSet } from "./metaDataThunks";

export const getReportConfiguration = createTypedAsyncThunk<ReportConfiguration | undefined>(
  "store/getReportConfiguration",
  (_, { getState }) => {
    const state = getState();
    const reportType = state.fieldsState.fieldsStateReducer.reportType;

    if (!reportType) {
      return;
    }
    if (usesTabularConfiguration(reportType)) {
      return createTabularReportConfiguration(
        state.fieldsState.tabularFieldsReducer.conditions,
        state.fieldsState.tabularFieldsReducer.fields,
        state.fieldsState.tabularFieldsReducer.sorts,
        state.fieldsState.tabularFieldsReducer.grouping,
        state.fieldsState.tabularFieldsReducer.settings
      );
    }

    if (isPivotReportType(reportType)) {
      return createPivotReportConfiguration(
        state.fieldsState.pivotFieldsReducer.conditions,
        state.fieldsState.pivotFieldsReducer.rows,
        state.fieldsState.pivotFieldsReducer.columns,
        state.fieldsState.pivotFieldsReducer.values,
        state.fieldsState.pivotFieldsReducer.sorts,
        state.fieldsState.pivotFieldsReducer.settings
      );
    }

    if (isChartReportType(reportType)) {
      return createChartReportConfiguration(
        state.fieldsState.chartFieldsReducer.conditions,
        state.fieldsState.chartFieldsReducer.arguments,
        state.fieldsState.chartFieldsReducer.values,
        state.fieldsState.chartFieldsReducer.sorts,
        state.fieldsState.chartFieldsReducer.settings
      );
    }

    if (isTileReportType(reportType)) {
      return createTileReportConfiguration(
        state.fieldsState.tileFieldsReducer.conditions,
        state.fieldsState.tileFieldsReducer.fields,
        state.fieldsState.tileFieldsReducer.settings
      );
    }

    if (isListReportType(reportType)) {
      return createListReportConfiguration(
        state.fieldsState.listFieldsReducer.conditions,
        state.fieldsState.listFieldsReducer.fields,
        state.fieldsState.listFieldsReducer.sorts,
        state.fieldsState.listFieldsReducer.settings,
        state.fieldsState.listFieldsReducer.ledger
      );
    }

    if (isFinancialStatementReportType(reportType)) {
      return createFinancialStatementReportConfiguration(state.fieldsState.financialStatementFieldsReducer);
    }

    throw new Error(`Unsupported report type: ${reportType}`);
  }
);

export const setEditReport = createTypedAsyncThunk("store/setEditReport", (report: Report, { dispatch, getState }) => {
  const state = getState();
  const newDataSetId = report.dataSetId || state.metaData.dataSets[0]?.dataSetId;
  const dataSetNeedsUpdate = newDataSetId && newDataSetId !== state.metaData.dataSetId;

  if (dataSetNeedsUpdate) {
    dispatch(changeDataSet(newDataSetId));
  }

  dispatch(createCurrentReportAction("setCurrentReport", report));
  //sets setFieldStatesConfiguration
  dispatch(
    fieldsStateActions.initializeFieldsState({
      configuration: report.configuration,
      reportType: report.reportType,
    })
  );
  // The editor fills in defaults on load, so compare unsaved changes against its configuration.
  dispatch(createCurrentReportAction("updateReport", { configuration: selectFieldsStateConfiguration(getState()) }));
  dispatch(createMetaDataAction("updateReportType", report.reportType));
  dispatch(baselineEditingHistory());
});

export const applyPreviewConfiguration = createTypedAsyncThunk<
  void,
  { datasetId: string; configuration: ReportConfiguration; reportType: ReportType }
>(
  "store/applyPreviewConfiguration",
  (
    {
      datasetId,
      configuration,
      reportType,
    }: { datasetId: string; configuration: ReportConfiguration; reportType: ReportType },
    { dispatch, getState }
  ) => {
    const state = getState();
    const newDataSetId = datasetId || state.metaData.dataSets[0]?.dataSetId;
    const dataSetNeedsUpdate = newDataSetId && newDataSetId !== state.metaData.dataSetId;
    if (dataSetNeedsUpdate) {
      dispatch(changeDataSet(newDataSetId));
    }
    dispatch(createCurrentReportAction("updateCurrentReportType", reportType));

    //sets setFieldStatesConfiguration
    dispatch(
      fieldsStateActions.initializeFieldsState({
        configuration: configuration,
        reportType: reportType,
      })
    );
    dispatch(createMetaDataAction("updateReportType", reportType));
  }
);

export const setViewReport = createTypedAsyncThunk("store/setViewReport", (report: Report, { dispatch, getState }) => {
  const state = getState();

  const newDataSetId = report.dataSetId || state.metaData.dataSets[0]?.dataSetId;
  const dataSetNeedsUpdate = newDataSetId && newDataSetId !== state.metaData.dataSetId;

  if (dataSetNeedsUpdate) {
    dispatch(changeDataSet(newDataSetId));
  }
  dispatch(createFieldsStateAction("setReportType", report.reportType));
  dispatch(createCurrentReportAction("refreshSession"));
  dispatch(createCurrentReportAction("setCurrentReport", report));
  dispatch(createMetaDataAction("updateReportType", report.reportType));
});

export const applyTemplate = createTypedAsyncThunk(
  "store/applyTemplate",
  (template: ReportTemplate, { dispatch, getState }) => {
    const state = getState();
    const newDataSetId = template.dataSetId || state.metaData.dataSets[0]?.dataSetId;
    const dataSetNeedsUpdate = newDataSetId && newDataSetId !== state.metaData.dataSetId;
    if (dataSetNeedsUpdate) {
      dispatch(changeDataSet(newDataSetId));
    }

    dispatch(createCurrentReportAction("applyTemplateOnReport", template));

    dispatch(
      fieldsStateActions.initializeFieldsState({
        configuration: template.configuration,
        reportType: template.reportType,
      })
    );
    dispatch(createMetaDataAction("updateReportType", template.reportType));
  }
);

export const updateReport = createTypedAsyncThunk(
  "store/updateReport",
  (changes: Partial<Report | ReportTemplate>, { dispatch }) => {
    dispatch(createCurrentReportAction("updateReport", changes));

    if (changes.reportType) {
      dispatch(createMetaDataAction("updateReportType", changes.reportType));
    }
  }
);

export const applyBiAgentConfiguration = createTypedAsyncThunk(
  "store/applyBiAgentConfiguration",
  (
    {
      configuration,
      reportType,
      dataSetId,
    }: { configuration: ReportConfiguration; reportType?: ReportType; dataSetId?: string },
    { dispatch, getState }
  ) => {
    const state = getState();
    const effectiveReportType = reportType ?? state.currentReport.report?.reportType;

    if (!effectiveReportType) {
      return;
    }

    if (dataSetId && dataSetId !== state.metaData.dataSetId) {
      dispatch(changeDataSet(dataSetId));
    }

    dispatch(createCurrentReportAction("updateReport", { configuration, reportType: effectiveReportType }));
    dispatch(createCurrentReportAction("refreshSession"));
    dispatch(
      fieldsStateActions.initializeFieldsState({
        configuration,
        reportType: effectiveReportType,
      })
    );
    dispatch(createMetaDataAction("updateReportType", effectiveReportType));
  }
);

export const getCurrentConfigurations = createTypedAsyncThunk("store/getCurrentConfigurations", (_, { getState }) => {
  const state = getState();
  const currentConfiguration = selectFieldsStateConfiguration(state);
  const isReportChanged = selectIsReportChanged(state);

  const currentReportType = selectReportType(state);

  const currentDataSetId = selectCurrentMetaDataSetId(state);

  if (!currentConfiguration || !currentReportType) {
    return {
      currentConfiguration: {
        arguments: [],
        values: [],
        sort: [],
        settings: createTabularDefaultSettings(),
        conditions: [],
        fields: [],
        columns: [],
        rows: [],
        grouping: [],
      },
      isReportChanged: false,
      currentReportType: currentReportType ?? ReportType.Tabular,
      error: "No current configuration",
      dataSetId: currentDataSetId ?? "",
    } satisfies ReportConfigurationState;
  }
  return {
    currentConfiguration: currentConfiguration,
    isReportChanged: isReportChanged,
    error: undefined,
    dataSetId: currentDataSetId ?? "",
    currentReportType,
  };
});
